// Role type picker order: types for the chosen industries first, then
// cross-industry types by family, then other industries' types by family.
// Nothing is hidden: a hospitality company can still pick Finance roles.
import { industryHeading } from './skills.ts';

export interface PickableRoleType {
  id: string;
  family: string;
  name: string;
  /** Industry ids; empty = any industry. */
  industries: string[];
}

export interface RoleTypeGroup<R extends PickableRoleType> {
  /** Unique across the result, so it can key a list. */
  heading: string;
  roleTypes: R[];
}

const byName = <R extends { name: string }>(a: R, b: R) => a.name.localeCompare(b.name);

function byFamily<R extends PickableRoleType>(roleTypes: R[], suffix = ''): RoleTypeGroup<R>[] {
  const map = new Map<string, R[]>();
  for (const r of roleTypes) map.set(r.family, [...(map.get(r.family) ?? []), r]);
  return [...map.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map((f) => ({ heading: `${f}${suffix}`, roleTypes: map.get(f)!.sort(byName) }));
}

/** `industries`: the chosen industries (id + name); empty = none yet. */
export function groupRoleTypesFor<R extends PickableRoleType>(
  roleTypes: readonly R[],
  industries: readonly { id: string; name: string }[],
): RoleTypeGroup<R>[] {
  if (industries.length === 0) return byFamily([...roleTypes]);
  const chosen = new Set(industries.map((i) => i.id));
  const forIndustry: R[] = [];
  const cross: R[] = [];
  const rest: R[] = [];
  for (const r of roleTypes) {
    if (r.industries.some((i) => chosen.has(i))) forIndustry.push(r);
    else if (r.industries.length === 0) cross.push(r);
    else rest.push(r);
  }
  return [
    ...(forIndustry.length
      ? [{ heading: industryHeading(industries), roleTypes: forIndustry.sort(byName) }]
      : []),
    ...byFamily(cross),
    ...byFamily(rest, ' · other industries'),
  ];
}

/** Picker label: a type its author added shows "(pending review)" or "(rejected)" (Q77). */
export function roleTypeLabel(rt: { name: string; status: string }) {
  if (rt.status === 'pending') return `${rt.name} (pending review)`;
  if (rt.status === 'rejected') return `${rt.name} (rejected)`;
  return rt.name;
}
